import React, { useEffect, useState, useRef, useCallback } from "react";

import { WebSocketConnection, ConnectionStatus } from "../../utils/WebSocketConnection";
import { DisplayMode } from "../../utils/baseConfig";
import { AudioMode } from "../../utils/audioUtils";
import { sendLinearFeedback } from "../../utils/miscEndpoints";
import SettingsView from "./SettingsView";
import FeedbackSentView from "./FeedbackSentView";
import SendFeedbackView from "./SendFeedbackView";

import xIcon from "../../assets/x.svg";
import "../../styles/components/sidepanel/SidePanel.css";

export enum SidePanelMode {
  Settings = "settings",
  Feedback = "feedback",
}

type SettingsPanelProps = {
  mode: SidePanelMode;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
  connection: WebSocketConnection | null;
  conversationId?: string;
  sessionId?: string;
  botName?: string;
  onAudioSpeakerChange: (deviceId: string) => void;
  onAudioSpeakerMuteChange: (muted: boolean) => void;
  audioSpeakerMuteGetter: () => boolean;
  onAudioSpeakerVolumeChange: (volume: number) => void;
  audioSpeakerVolumeGetter: () => number;
  setDisplayMode?: (mode: DisplayMode) => void;
  displayMode: DisplayMode;
  setAudioMode: (mode: AudioMode) => void;
  audioMode: AudioMode;
};

type DisplayState = "settings" | "feedback" | "feedbackSent";

const SidePanel: React.FC<SettingsPanelProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [displayState, setDisplayState] = useState<DisplayState>(props.mode);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [socketStatus, setSocketStatus] = useState<ConnectionStatus>(
    props.connection?.getStatus() || "disconnected"
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => { props.onClose(); }, 300);
  }, [props.onClose]);

  useEffect(() => {
    const conn = props.connection;
    conn?.addStatusListener("mic", setSocketStatus);
    return () => { conn?.removeStatusListener("mic"); };
  }, [props.connection]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        // Don't close if clicking on the trigger button
        if (props.triggerRef?.current && props.triggerRef.current.contains(event.target as Node)) {
          return;
        }
        event.stopPropagation();
        handleClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.triggerRef, handleClose]);

  const handleCancelFeedback = () => {
    handleClose();
  };

  const handleSendFeedback = async (feedbackText: string) => {
    if (feedbackText !== "" && props.conversationId && props.sessionId) {
      try {
        await sendLinearFeedback(props.sessionId, props.conversationId, feedbackText);
      } catch (error) {
        console.error("[Settings] Error sending feedback:", error);
      }
      setDisplayState("feedbackSent");
    }
  };

  const handleFeedbackButtonClick = () => {
    setDisplayState("feedback");
  };

  const title = displayState === "settings" ? "" : "Share Feedback";

  return (
    <div
      ref={ref}
      className={`sidepanel-overlay ${isClosing ? "closing" : ""}`}
      role="dialog"
      aria-modal="false" // Allow interaction with other elements
      aria-label="Settings"
    >
      <div className="sidepanel-content">
        {/* Header with close button */}
        <div className="sidepanel-header">
          <span className="sidepanel-title">{title}</span>
          <span onClick={handleClose} role="button" tabIndex={0} aria-label="Close settings">
            <img src={xIcon} alt="Close" className="sidepanel-close-icon" />
          </span>
        </div>

        {/* content */}
        {displayState === "settings" ? (
          <SettingsView
            onAudioSpeakerChange={props.onAudioSpeakerChange}
            onAudioSpeakerMuteChange={props.onAudioSpeakerMuteChange}
            audioSpeakerMuteGetter={props.audioSpeakerMuteGetter}
            onAudioSpeakerVolumeChange={props.onAudioSpeakerVolumeChange}
            audioSpeakerVolumeGetter={props.audioSpeakerVolumeGetter}
            setDisplayMode={props.setDisplayMode}
            displayMode={props.displayMode}
            setAudioMode={props.setAudioMode}
            audioMode={props.audioMode}
            webSocketStatus={socketStatus}
            handleFeedbackClick={handleFeedbackButtonClick}
          />
        ) : displayState === "feedback" ? (
          <SendFeedbackView
            onSubmitFeedback={handleSendFeedback}
            onCancelFeedback={handleCancelFeedback}
            botName={props.botName}
          />
        ) : (
          <FeedbackSentView />
        )}
      </div>
    </div>
  );
};

export default SidePanel;
